import React, { useEffect } from 'react'

import {
  pantryColors,
  pantryLayout,
  pantryShadows,
  pantryTypography,
} from '../../../foundations/tokens'

interface SlideDrawerProps {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  /**
   * Optional inline adornment rendered to the right of the title text
   * (e.g. an icon-link). Sits inside the drawer header so it scrolls and
   * slides with the panel. Kept additive so existing consumers are
   * unaffected.
   */
  titleAdornment?: React.ReactNode
  footer?: React.ReactNode
  width?: string | number
  children?: React.ReactNode
  zIndex?: number
}

export const SlideDrawer: React.FC<SlideDrawerProps> = ({
  open,
  onClose,
  title,
  subtitle,
  titleAdornment,
  footer,
  width = 480,
  children,
  zIndex,
}) => {
  const backdropZ = zIndex != null ? zIndex : 40
  const panelZ = zIndex != null ? zIndex + 1 : 50
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (open) document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: backdropZ,
          background: 'rgba(17, 24, 39, 0.4)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.2s',
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        style={{
          position: 'fixed',
          inset: '0 0 0 auto',
          zIndex: panelZ,
          display: 'flex',
          flexDirection: 'column',
          width: width ?? pantryLayout.drawerWidth,
          maxWidth: '100%',
          background: pantryColors.white,
          boxShadow: pantryShadows.lg,
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease-in-out',
        }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            padding: '16px 24px',
            borderBottom: `1px solid ${pantryColors.gray[200]}`,
          }}
        >
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: 17,
                  fontWeight: 700,
                  color: pantryColors.gray[800],
                  fontFamily: pantryTypography.fontFamily,
                }}
              >
                {title}
              </h2>
              {titleAdornment}
            </div>
            {subtitle && (
              <p
                style={{
                  margin: '2px 0 0',
                  fontSize: 12,
                  color: pantryColors.gray[400],
                  fontFamily: pantryTypography.fontFamily,
                }}
              >
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              marginLeft: 16,
              border: 'none',
              background: 'transparent',
              color: pantryColors.gray[400],
              cursor: 'pointer',
            }}
            aria-label="Close drawer"
          >
            <svg
              width="20"
              height="20"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px 24px',
          }}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            style={{
              padding: '16px 24px',
              borderTop: `1px solid ${pantryColors.gray[200]}`,
              background: pantryColors.gray[50],
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </>
  )
}
